import React, { useEffect, useState, useMemo } from "react";
import { api, CATEGORY_LABELS, CATEGORIES } from "@/lib/api";
import NoteCard from "@/components/app/NoteCard";
import OfferDialog from "@/components/app/OfferDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSEO, jsonLdBreadcrumb } from "@/lib/seo";

const YIELD_OPTS = [
  { v: "", l: "Any" },
  { v: "10", l: "10%+" },
  { v: "15", l: "15%+" },
  { v: "20", l: "20%+" },
  { v: "25", l: "25%+" },
];
const SORT_OPTS = [
  { v: "yield_desc", l: "Highest Yield" },
  { v: "yield_asc", l: "Lowest Yield" },
  { v: "balance_desc", l: "Highest Balance" },
  { v: "balance_asc", l: "Lowest Balance" },
  { v: "newest", l: "Newest Listings" },
  { v: "oldest", l: "Oldest Listings" },
  { v: "lowest_risk", l: "Lowest Risk" },
  { v: "highest_risk", l: "Highest Risk" },
];

export default function AvailableNotes() {
  const [items, setItems] = useState([]);
  const [filterOpts, setFilterOpts] = useState({ states: [], makes: [], models: [], years: [] });
  const [category, setCategory] = useState("");
  const [minYield, setMinYield] = useState("");
  const [states, setStates] = useState([]);
  const [grades, setGrades] = useState([]);
  const [vehicleYear, setVehicleYear] = useState("");
  const [vehicleMake, setVehicleMake] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [minBalance, setMinBalance] = useState("");
  const [maxBalance, setMaxBalance] = useState("");
  const [minWeekly, setMinWeekly] = useState("");
  const [maxWeekly, setMaxWeekly] = useState("");
  const [minTerm, setMinTerm] = useState("");
  const [maxTerm, setMaxTerm] = useState("");
  const [minRate, setMinRate] = useState("");
  const [maxRate, setMaxRate] = useState("");
  const [sort, setSort] = useState("yield_desc");
  const [offerNote, setOfferNote] = useState(null);
  const [loading, setLoading] = useState(false);

  useSEO({
    title: "Available Vehicle Notes for Sale — Buy Cash-Flow Auto Notes | FinanceLocal",
    description: "Browse cash-flowing vehicle-backed notes for sale. Filter by yield, grade, state, and category. Buy performing, semi-performing, non-performing, and fresh-origination auto notes on the FinanceLocal marketplace.",
    canonical: "https://myfinancelocal.com/notes",
    keywords: "vehicle notes for sale, buy performing notes, cash-flow notes for sale, auto note marketplace, non-performing notes, note buyers, note investors",
    jsonLd: jsonLdBreadcrumb([
      { name: "Home", url: "https://myfinancelocal.com/" },
      { name: "Available Notes", url: "https://myfinancelocal.com/notes" },
    ]),
  });

  useEffect(() => {
    api.get("/notes/filters/options").then((r) => setFilterOpts(r.data)).catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (category) p.category = category;
    if (minYield) p.min_yield = minYield;
    if (states.length) p.states = states.join(",");
    if (grades.length) p.grade = grades.join(",");
    if (vehicleYear) p.vehicle_year = vehicleYear;
    if (vehicleMake) p.vehicle_make = vehicleMake;
    if (vehicleModel) p.vehicle_model = vehicleModel;
    if (minBalance) p.min_balance = minBalance;
    if (maxBalance) p.max_balance = maxBalance;
    if (minWeekly) p.min_weekly = minWeekly;
    if (maxWeekly) p.max_weekly = maxWeekly;
    if (minTerm) p.min_term = minTerm;
    if (maxTerm) p.max_term = maxTerm;
    if (minRate) p.min_rate = minRate;
    if (maxRate) p.max_rate = maxRate;
    p.sort = sort;
    return p;
  }, [category, minYield, states, grades, vehicleYear, vehicleMake, vehicleModel, minBalance, maxBalance, minWeekly, maxWeekly, minTerm, maxTerm, minRate, maxRate, sort]);

  useEffect(() => {
    setLoading(true);
    api.get("/notes", { params }).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, [params]);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const reset = () => {
    setCategory(""); setMinYield(""); setStates([]); setGrades([]);
    setVehicleYear(""); setVehicleMake(""); setVehicleModel("");
    setMinBalance(""); setMaxBalance(""); setMinWeekly(""); setMaxWeekly("");
    setMinTerm(""); setMaxTerm(""); setMinRate(""); setMaxRate(""); setSort("yield_desc");
  };

  return (
    <div data-testid="page-available-notes" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Marketplace</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Available Notes</h1>
        <p className="text-muted-foreground mt-2">Filter, evaluate, and submit offers on cash-flowing vehicle notes.</p>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-4" data-testid="category-tabs">
        <CatTab active={category === ""} onClick={() => setCategory("")} label="All" testId="cat-all" />
        {CATEGORIES.map((c) => (
          <CatTab key={c} active={category === c} onClick={() => setCategory(c)}
            label={CATEGORY_LABELS[c]} testId={`cat-${c}`} />
        ))}
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="space-y-6" data-testid="filters-sidebar">
          <FilterBlock title="Target Yield">
            <Select value={minYield || "any"} onValueChange={(v) => setMinYield(v === "any" ? "" : v)}>
              <SelectTrigger className="rounded-none" data-testid="filter-yield"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {YIELD_OPTS.map((o) => <SelectItem key={o.v || "any"} value={o.v || "any"}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterBlock>

          <FilterBlock title="Grade">
            <div className="grid grid-cols-2 gap-2" data-testid="filter-grade">
              {["A", "B", "C", "D"].map((g) => (
                <label key={g} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={grades.includes(g)} onCheckedChange={() => toggle(grades, setGrades, g)}
                    data-testid={`filter-grade-${g}`} className="rounded-none" />
                  <span>Grade {g}</span>
                </label>
              ))}
            </div>
          </FilterBlock>

          <FilterBlock title="State">
            <div className="max-h-40 overflow-y-auto pr-1 grid grid-cols-2 gap-1" data-testid="filter-state">
              {filterOpts.states.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={states.includes(s)} onCheckedChange={() => toggle(states, setStates, s)}
                    className="rounded-none" data-testid={`filter-state-${s}`} />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </FilterBlock>

          <FilterBlock title="Vehicle">
            <div className="space-y-2">
              <Input placeholder="Year" value={vehicleYear} onChange={(e) => setVehicleYear(e.target.value)}
                className="rounded-none" data-testid="filter-year" />
              <Input placeholder="Make" value={vehicleMake} onChange={(e) => setVehicleMake(e.target.value)}
                list="makes-list" className="rounded-none" data-testid="filter-make" />
              <datalist id="makes-list">{filterOpts.makes.map((m) => <option key={m} value={m} />)}</datalist>
              <Input placeholder="Model" value={vehicleModel} onChange={(e) => setVehicleModel(e.target.value)}
                className="rounded-none" data-testid="filter-model" />
            </div>
          </FilterBlock>

          <FilterBlock title="Financial">
            <RangePair label="Balance" a={minBalance} b={maxBalance} setA={setMinBalance} setB={setMaxBalance} tid="balance" />
            <RangePair label="Weekly Pmt" a={minWeekly} b={maxWeekly} setA={setMinWeekly} setB={setMaxWeekly} tid="weekly" />
            <RangePair label="Term (wk)" a={minTerm} b={maxTerm} setA={setMinTerm} setB={setMaxTerm} tid="term" />
            <RangePair label="Rate %" a={minRate} b={maxRate} setA={setMinRate} setB={setMaxRate} tid="rate" />
          </FilterBlock>

          <Button data-testid="filter-reset" variant="outline" onClick={reset} className="w-full rounded-none uppercase text-xs tracking-wider">Reset Filters</Button>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono-data text-sm text-muted-foreground" data-testid="results-count">
              {loading ? "Loading…" : `${items.length} note${items.length === 1 ? "" : "s"}`}
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[200px] rounded-none" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {SORT_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {items.length === 0 && !loading ? (
            <div className="border border-border p-12 text-center text-muted-foreground" data-testid="empty-state">
              No notes match your filters.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="notes-grid">
              {items.map((n) => <NoteCard key={n.note_id} note={n} onMakeOffer={setOfferNote} />)}
            </div>
          )}
        </div>
      </div>

      <OfferDialog note={offerNote} open={!!offerNote} onOpenChange={(o) => !o && setOfferNote(null)} />
    </div>
  );
}

function CatTab({ active, onClick, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`px-4 py-2 text-xs uppercase tracking-wider border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-muted"}`}>
      {label}
    </button>
  );
}

function FilterBlock({ title, children }) {
  return (
    <div>
      <Label className="font-overline mb-3 block">{title}</Label>
      {children}
    </div>
  );
}

function RangePair({ label, a, b, setA, setB, tid }) {
  return (
    <div className="mb-2">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Min" value={a} onChange={(e) => setA(e.target.value)}
          className="rounded-none h-9" data-testid={`filter-${tid}-min`} />
        <Input placeholder="Max" value={b} onChange={(e) => setB(e.target.value)}
          className="rounded-none h-9" data-testid={`filter-${tid}-max`} />
      </div>
    </div>
  );
}
